/**
 * src/infra/api/apiClient.ts
 *
 * Lightweight API client for Pioneer Marketplace.
 * Auth token is obtained via cross-subdomain handoff (AuthBootstrap)
 * and stored through the auth domain helpers.
 */

import { getToken, getSelectedOrg, removeToken } from '../../domains/auth';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const orgId = getSelectedOrg();
  if (orgId) {
    headers['x-org-id'] = orgId;
  }
  return headers;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: { ...getAuthHeaders(), ...(options.headers || {}) },
    credentials: 'include',
  });
  if (res.status === 401) {
    // Redirect to Pioneer sign-in with returnTo so user comes back
    const pioneerUrl =
      process.env.REACT_APP_PIONEER_URL || 'https://pioneer-staging.transcent.in';
    const returnTo = encodeURIComponent(window.location.href);
    // Clear stale token so AuthBootstrap doesn't loop
    removeToken();
    window.location.href = `${pioneerUrl}/signin?returnTo=${returnTo}`;
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed: ${res.status}`);
  }
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : (null as T);
}

export const apiClient = {
  get<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'GET' });
  },
  post<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
  },
  put<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
  },
  patch<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
  },
  delete<T>(path: string): Promise<T | null> {
    return request<T>(path, { method: 'DELETE' });
  },
};
